import { ElementType, memo, ReactNode } from 'react';
import { Box, Flex } from '@chakra-ui/react';

import { accent, font, neutral, radius, text } from 'styles/revampTokens';

import {
  BlockNavigationClickOptions,
  BlockNavigationDropdownItem,
} from './BlockNavigationDropdown';
import { compactAgo, initialsOf } from './dashboardNavDropdownShared';

import type { DashboardNavPack } from './dashboardNavDropdownShared';

const DropdownBox = Box as ElementType;
const DropdownFlex = Flex as ElementType;

const ArrowIcon = () => (
  <svg width='16' height='16' viewBox='0 0 20 20' fill='none'>
    <path
      d='M11.667 5.008L10.492 6.183l2.975 2.984H3.333v1.666h10.134l-2.975 2.984 1.175 1.175L16.667 10l-5-4.992Z'
      fill='currentColor'
    />
  </svg>
);

const ClockIcon = () => (
  <svg width='13' height='13' viewBox='0 0 14 14' fill='none'>
    <circle cx='7' cy='7' r='5.6' stroke='currentColor' strokeWidth='1.1' />
    <path d='M7 4V7L9 8.2' stroke='currentColor' strokeWidth='1.1' strokeLinecap='round' />
  </svg>
);

interface DashboardNavigationPreviewPaneProperties {
  previewPage: BlockNavigationDropdownItem | null;
  activePack: DashboardNavPack | undefined;
  previewTint: { bg: string; fg: string };
  packActions: ReactNode;
  allInstruments?: any[];
  onPageClick: (page: BlockNavigationDropdownItem, options?: BlockNavigationClickOptions) => void;
}

const DashboardNavigationPreviewPane = memo(
  ({
    previewPage,
    activePack,
    previewTint,
    packActions,
    allInstruments,
    onPageClick,
  }: DashboardNavigationPreviewPaneProperties) => {
    if (previewPage) {
      const pageInstruments = Array.isArray(allInstruments)
        ? allInstruments.filter((inst: any) => String(inst?.page_id) === String(previewPage.id))
        : [];
      const total = pageInstruments.length;

      return (
        <DropdownFlex direction='column' p='14px' gap='10px'>
          <DropdownFlex alignItems='center' gap='10px'>
            <DropdownFlex
              w='32px'
              h='32px'
              flexShrink={0}
              alignItems='center'
              justifyContent='center'
              borderRadius={radius.control}
              bg={previewTint.bg}
              color={previewTint.fg}
              fontFamily={font.body}
              fontSize='12px'
              fontWeight='700'
            >
              {initialsOf(previewPage.title)}
            </DropdownFlex>
            <DropdownFlex direction='column' gap='2px' flex='1' minW={0}>
              <DropdownBox
                color={text.primary}
                fontFamily={font.display}
                fontSize='14px'
                fontWeight='600'
                noOfLines={1}
              >
                {previewPage.title}
              </DropdownBox>
              {activePack && (
                <DropdownBox color={text.secondary} fontFamily={font.body} fontSize='11px'>
                  {activePack.title}
                </DropdownBox>
              )}
            </DropdownFlex>
          </DropdownFlex>

          {total > 0 && (
            <DropdownFlex
              alignItems='center'
              justifyContent='space-between'
              px='10px'
              py='7px'
              border={`1px solid ${neutral.border}`}
              borderRadius={radius.control}
              bg={neutral.surface}
            >
              <DropdownBox color={text.secondary} fontFamily={font.body} fontSize='12px'>
                Total widgets
              </DropdownBox>
              <DropdownBox
                color={text.primary}
                fontFamily={font.body}
                fontSize='13px'
                fontWeight='700'
              >
                {total}
              </DropdownBox>
            </DropdownFlex>
          )}

          <DropdownFlex
            as='button'
            type='button'
            alignItems='center'
            justifyContent='center'
            gap='6px'
            h='38px'
            borderRadius={radius.control}
            bg={accent.solid}
            color='#FFFFFF'
            fontFamily={font.body}
            fontSize='13px'
            fontWeight='600'
            cursor='pointer'
            _hover={{ bg: accent.hover }}
            onClick={() => onPageClick(previewPage)}
          >
            Go to page
            <ArrowIcon />
          </DropdownFlex>
        </DropdownFlex>
      );
    }

    if (activePack) {
      return (
        <DropdownFlex direction='column' p='14px' gap='12px'>
          <DropdownFlex alignItems='center' gap='10px'>
            <DropdownFlex
              w='32px'
              h='32px'
              alignItems='center'
              justifyContent='center'
              borderRadius={radius.control}
              bg={previewTint.bg}
              color={previewTint.fg}
              fontFamily={font.body}
              fontSize='12px'
              fontWeight='700'
            >
              {initialsOf(activePack.title)}
            </DropdownFlex>
            <DropdownBox
              color={text.primary}
              fontFamily={font.display}
              fontSize='14px'
              fontWeight='600'
            >
              {activePack.title}
            </DropdownBox>
          </DropdownFlex>
          {activePack.description && (
            <DropdownBox color={text.secondary} fontFamily={font.body} fontSize='12px'>
              {activePack.description}
            </DropdownBox>
          )}
          {compactAgo(activePack.updated_at) && (
            <DropdownFlex alignItems='center' gap='7px' color={text.secondary}>
              <ClockIcon />
              <DropdownBox fontFamily={font.body} fontSize='12px'>
                Updated {compactAgo(activePack.updated_at)}
              </DropdownBox>
            </DropdownFlex>
          )}
          {packActions}
        </DropdownFlex>
      );
    }

    return (
      <DropdownBox color={text.secondary} fontSize='13px' m='auto' textAlign='center' p='16px'>
        Select a dashboard pack
      </DropdownBox>
    );
  },
);

DashboardNavigationPreviewPane.displayName = 'DashboardNavigationPreviewPane';

export default DashboardNavigationPreviewPane;
